import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Globe, Code, Smartphone, Bot, ArrowRight, CheckCircle } from 'lucide-react'
import { Button } from '../components/ui/button'

export const Route = createFileRoute('/solutions')({
  component: SolutionsPage,
})

const solutions = [
  {
    icon: Globe,
    title: "Website Development",
    description: "Custom, high-performance websites built with modern technologies like React, Next.js, and Tailwind CSS. We focus on speed, SEO, and user experience.",
    features: [
      "Responsive design for all devices",
      "SEO optimization built-in",
      "Fast loading with modern frameworks",
      "Custom CMS integration",
      "E-commerce capabilities",
      "Analytics and tracking setup"
    ],
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: Code,
    title: "Software Development",
    description: "Scalable enterprise software solutions tailored to your business needs. From backend architecture to intuitive frontends, we build full-cycle applications.",
    features: [
      "Custom business applications",
      "Database design & optimization",
      "API development & integration",
      "Cloud-native architecture",
      "Legacy system modernization",
      "Automated testing & CI/CD"
    ],
    color: "from-indigo-500 to-purple-500"
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    description: "Native and cross-platform mobile applications for iOS and Android. We deliver seamless mobile experiences that engage your users.",
    features: [
      "iOS & Android development",
      "Cross-platform with React Native",
      "Offline-first capabilities",
      "Push notifications",
      "App Store optimization",
      "Performance optimization"
    ],
    color: "from-pink-500 to-rose-500"
  },
  {
    icon: Bot,
    title: "AI and API Integration",
    description: "Combine Artificial Intelligence with robust API integrations to automate workflows, extract insights, and securely connect your platforms.",
    features: [
      "ChatGPT & LLM integration",
      "Custom AI model development",
      "Workflow automation",
      "Third-party API integration",
      "Data pipeline development",
      "Real-time data processing"
    ],
    color: "from-amber-500 to-orange-500"
  }
]

function SolutionsPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <p className="text-sm font-medium text-indigo-400">Our Solutions</p>
          <h1 className="text-4xl md:text-6xl font-bold">
            Build Your Digital Future
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            From websites to AI-powered applications, we deliver end-to-end solutions
            that drive growth and transform your business.
          </p>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="relative z-10 py-16 container mx-auto px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          {solutions.map((solution, index) => {
            const Icon = solution.icon
            return (
              <div
                key={index}
                className="group bg-slate-900/30 border border-white/5 rounded-3xl p-8 hover:border-white/10 transition-all duration-300"
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-linear-to-br ${solution.color} shrink-0`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">{solution.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{solution.description}</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {solution.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3">
                      <CheckCircle className="h-4 w-4 text-indigo-400 shrink-0" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button asChild variant="outline" className="w-full group/btn border-white/10 hover:border-indigo-500/50 hover:bg-indigo-500/10">
                  <Link to="/contact" className="flex items-center justify-center gap-2">
                    Get Started
                    <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </div>
            )
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center bg-linear-to-b from-indigo-500/10 to-blue-500/10 border border-white/5 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-slate-400 mb-8">
            Let's discuss your project and find the perfect solution for your business needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
              <Link to="/contact">Book Free Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/10 hover:bg-white/5">
              <Link to="/pricing">View Pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
